import { memo } from "react";
import type { ButtonProps, ButtonVariant, ButtonSize } from "../types";
import { colors, radius, spacing, transitions, typography } from "../tokens";

const variantStyles: Record<ButtonVariant, string> = {
  primary: `background:${colors.primary};color:${colors.textInverse};border:2px solid ${colors.primary};`,
  secondary: `background:transparent;color:${colors.text};border:2px solid ${colors.surfaceBorder};`,
  ghost: `background:transparent;color:${colors.textMuted};border:2px solid transparent;`,
  danger: `background:${colors.error};color:${colors.text};border:2px solid ${colors.error};`,
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: `padding:${spacing.xs} ${spacing.md};font-size:${typography.size.sm};`,
  md: `padding:${spacing.sm} ${spacing.xl};font-size:${typography.size.base};`,
  lg: `padding:${spacing.md} ${spacing["2xl"]};font-size:${typography.size.lg};`,
};

function parseCssString(css: string): Record<string, string> {
  return Object.fromEntries(
    css
      .split(";")
      .filter(Boolean)
      .map(rule => {
        const [k, ...v] = rule.split(":");
        return [
          k!.trim().replace(/-([a-z])/g, (_, c: string) => c.toUpperCase()),
          v.join(":").trim(),
        ];
      })
  );
}

export const Button = memo(function Button({
  variant = "primary",
  size = "md",
  loading = false,
  fullWidth = false,
  disabled,
  children,
  style,
  ...props
}: ButtonProps) {
  const isDisabled = disabled || loading;

  return (
    <button
      {...props}
      disabled={isDisabled}
      aria-busy={loading}
      style={{
        fontFamily: typography.fontFamily,
        fontWeight: typography.weight.semibold,
        borderRadius: radius.md,
        cursor: isDisabled ? "not-allowed" : "pointer",
        transition: `all ${transitions.fast}`,
        opacity: isDisabled ? 0.5 : 1,
        width: fullWidth ? "100%" : undefined,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: spacing.sm,
        whiteSpace: "nowrap",
        ...parseCssString(variantStyles[variant]),
        ...parseCssString(sizeStyles[size]),
        ...style,
      }}
    >
      {loading ? <Spinner /> : children}
    </button>
  );
});

function Spinner() {
  return (
    <span
      role="status"
      aria-label="Loading"
      style={{
        width: "1em",
        height: "1em",
        border: `2px solid currentColor`,
        borderTopColor: "transparent",
        borderRadius: "50%",
        display: "inline-block",
        animation: "spin 0.7s linear infinite",
      }}
    />
  );
}
